import { isProjectId } from './project-path';
import { htmlToPlainText, isRichTextEmpty } from './rich-text';
import { UUID_RE } from './work-types';

export type OpsItemType = 'incident' | 'problem';

export function isOpsItemType(type: string): type is OpsItemType {
  return type === 'incident' || type === 'problem';
}

export type OpsCreateQuery = { projectId?: string; stateId?: string };

function withCreateQuery(path: string, opts?: OpsCreateQuery): string {
  const params = new URLSearchParams();
  if (opts?.projectId && isProjectId(opts.projectId)) {
    params.set('project', opts.projectId);
  }
  if (opts?.stateId && UUID_RE.test(opts.stateId)) {
    params.set('state', opts.stateId);
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

/** Title hint on New incident. System + symptom, not a user story. */
export const INCIDENT_TITLE_PLACEHOLDER = 'What broke — system and symptom';

/** Title hint on New problem. Recurring cause, not one fire. */
export const PROBLEM_TITLE_PLACEHOLDER = 'Recurring cause — not one fire';

export const INCIDENT_CREATE_PATH = '/incidents/new';
export const PROBLEM_CREATE_PATH = '/problems/new';

export const INCIDENT_DESCRIPTION_TEMPLATE = [
  '<h3>What failed</h3>',
  '<p>[service or symptom]</p>',
  '<h3>Impact</h3>',
  '<p>[who or what is hit]</p>',
  '<h3>Evidence</h3>',
  '<ul><li>[time, error, host, or screenshot]</li></ul>',
  '<h3>Mitigation</h3>',
  '<p>[what you already did, or none yet]</p>',
].join('');

export const PROBLEM_DESCRIPTION_TEMPLATE = [
  '<h3>What\'s recurring</h3>',
  '<p>[the underlying defect or pattern]</p>',
  '<h3>How it shows up</h3>',
  '<p>[symptoms or incident pattern]</p>',
  '<h3>Evidence</h3>',
  '<ul><li>[times, keys, logs]</li></ul>',
  '<h3>Workaround</h3>',
  '<p>[what we do until a lasting fix, or none yet]</p>',
].join('');

export const OPS_CREATE = {
  incident: {
    type: 'incident' as const,
    listPath: '/incidents',
    createPath: INCIDENT_CREATE_PATH,
    listLabel: 'Incidents',
    pageTitle: 'New incident',
    titlePlaceholder: INCIDENT_TITLE_PLACEHOLDER,
    titleAriaLabel: 'Incident title',
    createLabel: 'Create incident',
    joinError: 'Join a stream before filing an incident.',
    missingTitle: 'Name what broke before creating.',
    missingProject: 'Choose a stream to file this incident.',
    createFailed: 'Failed to create incident',
    missingId: 'Create succeeded without an incident id.',
    fieldId: 'incident',
    descriptionTemplate: INCIDENT_DESCRIPTION_TEMPLATE,
  },
  problem: {
    type: 'problem' as const,
    listPath: '/problems',
    createPath: PROBLEM_CREATE_PATH,
    listLabel: 'Problems',
    pageTitle: 'New problem',
    titlePlaceholder: PROBLEM_TITLE_PLACEHOLDER,
    titleAriaLabel: 'Problem title',
    createLabel: 'Create problem',
    joinError: 'Join a stream before filing a problem.',
    missingTitle: 'Name the recurring cause before creating.',
    missingProject: 'Choose a stream to file this problem.',
    createFailed: 'Failed to create problem',
    missingId: 'Create succeeded without a problem id.',
    fieldId: 'problem',
    descriptionTemplate: PROBLEM_DESCRIPTION_TEMPLATE,
  },
} as const;

export function opsCreatePath(type: OpsItemType, opts?: OpsCreateQuery): string {
  return withCreateQuery(OPS_CREATE[type].createPath, opts);
}

export function incidentCreatePath(opts?: OpsCreateQuery): string {
  return opsCreatePath('incident', opts);
}

export function problemCreatePath(opts?: OpsCreateQuery): string {
  return opsCreatePath('problem', opts);
}

export function isOpsCreatePath(pathname: string): boolean {
  return pathname === INCIDENT_CREATE_PATH || pathname === PROBLEM_CREATE_PATH;
}

export function isIncidentCreatePath(pathname: string): boolean {
  return pathname === INCIDENT_CREATE_PATH;
}

export function isProblemCreatePath(pathname: string): boolean {
  return pathname === PROBLEM_CREATE_PATH;
}

function templateFingerprint(html: string): string {
  return htmlToPlainText(html)
    .toLowerCase()
    .replace(/[\u200b\u00a0]/g, ' ')
    .replace(/[’‘]/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

function isUnusedTemplate(html: string, template: string): boolean {
  if (typeof html !== 'string') return false;
  if (isRichTextEmpty(html)) return false;
  return templateFingerprint(html) === templateFingerprint(template);
}

export function isIncidentDescriptionTemplate(html: string): boolean {
  return isUnusedTemplate(html, INCIDENT_DESCRIPTION_TEMPLATE);
}

export function isProblemDescriptionTemplate(html: string): boolean {
  return isUnusedTemplate(html, PROBLEM_DESCRIPTION_TEMPLATE);
}

export function isUnusedOpsDescription(type: string, html: string): boolean {
  if (type === 'incident') return isIncidentDescriptionTemplate(html);
  if (type === 'problem') return isProblemDescriptionTemplate(html);
  return false;
}

export function initialDescriptionForType(type: string): string {
  if (type === 'incident') return INCIDENT_DESCRIPTION_TEMPLATE;
  if (type === 'problem') return PROBLEM_DESCRIPTION_TEMPLATE;
  return '';
}

/** Drop the unused scaffold so we do not persist placeholder copy. */
export function descriptionForCreate(type: string, html: string): string {
  if (isUnusedOpsDescription(type, html)) return '';
  return html;
}
